import {
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Grid,
    IconButton,
    Tooltip
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { withTheme } from '@material-ui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { formatMoney } from '../utils';
import useOnScreen from '../hooks/useOnScreen';
import { BookmarkIcon, CompareIcon, CompareRightIcon, FireIcon } from '../icons';
import { convertToFinalPrice } from '../finalPrice';

/**
 * Calculate a minimum price to show on the card
 * @param firstTimeslots includes a ticket prices
 * @param tickets list of tickets
 * @returns {number|*}
 */
function calculateMinRetailPrice(firstTimeslots, tickets) {
    let price = -1;

    const filteredTickets = tickets
        ?.filter(e => e.retail_price > 0)
        .sort((a, b) => {
            const aPrice = convertToFinalPrice(a.retail_price, a?.final_price);
            const bPrice = convertToFinalPrice(b.retail_price, b?.final_price);
            const aIsAdultPrice = a.name.toLowerCase().includes('adult');
            const bIsAdultPrice = b.name.toLowerCase().includes('adult');

            if (aIsAdultPrice && !bIsAdultPrice) {
                return -1;
            }
            if (!aIsAdultPrice && bIsAdultPrice) {
                return 1;
            }
            return aPrice - bPrice;
        });
    if (filteredTickets?.length > 0) {
        price = convertToFinalPrice(
            filteredTickets[0].retail_price,
            filteredTickets[0]?.final_price
        );
    } else {
        price = 0;
    }
    return price;
}

function ActivityCard({
    activity,
    currentFilters,
    theme,
    onClick,
    isCompared,
    toggleCompare,
    comparisonEnabled = true,
    children
}) {
    const cardRef = useRef();
    const [showImage, setShowImage] = useState(false);
    const isIntersecting = useOnScreen(cardRef);
    useEffect(() => {
        setShowImage(e => e || isIntersecting);
    }, [isIntersecting, setShowImage]);

    // start from enabled if we do not hide inactives, and start from disabled when we hide
    // this way in hide case activities start to become enabled (visible) after loading
    const enabled = !!activity?.firstTimeslot;

    // if we need to hide, don't even add it to the dom
    if (!currentFilters.showEmpty && !enabled) {
        return null;
    }

    const minRetailPrice = calculateMinRetailPrice(activity.firstTimeslot, activity.tickets);

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Card
                ref={cardRef}
                elevation={1}
                rounded="true"
                style={{
                    height: '100%',
                    position: 'relative'
                }}
            >
                <CardActions style={{ padding: '16px 16px 8px 16px' }}>
                    <h5 style={{ color: '#333' }}>
                        <Link
                            to={`/activities/vendor/${activity?.company?.id}`}
                            style={{ color: 'rgb(51, 51, 51)' }}
                        >
                            {activity?.company?.name}
                        </Link>{' '}
                        (
                        {activity?.island?.name === 'Hawaiʻi'
                            ? `${activity?.island?.name} island`
                            : activity?.island?.name}
                        )
                    </h5>
                    {activity?.featured && (
                        <Tooltip title="Featured" placement="bottom-end">
                            <BookmarkIcon
                                style={{
                                    color: theme.palette.primary.main,
                                    position: 'absolute',
                                    right: 5,
                                    top: -3,
                                    fontSize: '1.2em'
                                }}
                            />
                        </Tooltip>
                    )}
                    {/* Agents only get the flag; it is why a boosted card sits up top. */}
                    {activity?.hasCommissionBoost && (
                        <Tooltip
                            title={`Extra commission up to ${formatMoney(
                                (activity.maxExtraCommission || 0) / 100
                            )}`}
                            placement="bottom-end"
                        >
                            <FireIcon
                                style={{
                                    color: '#e65100',
                                    position: 'absolute',
                                    right: activity?.featured ? 28 : 5,
                                    top: -3,
                                    fontSize: '1.2em'
                                }}
                            />
                        </Tooltip>
                    )}
                </CardActions>
                <CardMedia
                    style={{ height: '200px', cursor: 'pointer' }}
                    image={showImage ? activity.image : null}
                    onClick={onClick}
                >
                    <div
                        style={
                            enabled
                                ? { opacity: 0, transition: 'opacity 0.3s' }
                                : {
                                      opacity: 1,
                                      transition: 'opacity 0.3s',
                                      height: '200px',
                                      background: 'rgba(255, 255, 255,0.6)'
                                  }
                        }
                    >
                        <h3 style={{ textAlign: 'center', paddingTop: '80px' }}>No timeslot</h3>
                    </div>
                </CardMedia>
                <CardContent className="px-2">
                    {comparisonEnabled && (
                        <Tooltip title="Compare Activities">
                            <IconButton
                                className={`compare-icon ${isCompared ? 'compared' : ''}`}
                                // TODO: refactor, so we don't need to pass minRetailPrice
                                onClick={() => toggleCompare(minRetailPrice)}
                            >
                                {isCompared ? (
                                    <CompareRightIcon
                                        style={{ color: theme.palette.primary.main }}
                                    />
                                ) : (
                                    <CompareIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    <h5>From {formatMoney(minRetailPrice)}</h5>
                    <h4 style={{ color: !enabled ? theme.palette.text.disabled : '' }}>
                        {activity.name}
                    </h4>
                    {children}
                </CardContent>
            </Card>
        </Grid>
    );
}

export default withTheme(ActivityCard);
